"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Camera as CameraIcon, Crosshair, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Roi,
  ServicePhoto,
  backendErrorMessage,
  useSetFocus,
  useTakePhoto,
} from "./api";
import { useLocalization } from "@/context/localization-context";
import { translate } from "@/i18n/translate";

interface PhotoPanelProps {
  deviceId: string;
}

/** Normalized (0..1) drag-rectangle state while the user is drawing. */
interface DragState {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

const dragToRoi = (drag: DragState): Roi => ({
  x: Math.min(drag.startX, drag.currentX),
  y: Math.min(drag.startY, drag.currentY),
  width: Math.abs(drag.currentX - drag.startX),
  height: Math.abs(drag.currentY - drag.startY),
});

const roundRoi = (roi: Roi): Roi => ({
  x: Number(roi.x.toFixed(3)),
  y: Number(roi.y.toFixed(3)),
  width: Number(roi.width.toFixed(3)),
  height: Number(roi.height.toFixed(3)),
});

export const PhotoPanel = ({ deviceId }: PhotoPanelProps) => {
  const { t, formatNumber, intlLocale } = useLocalization();
  const [photo, setPhoto] = useState<ServicePhoto | null>(null);
  const [quality, setQuality] = useState("85");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshSeconds, setRefreshSeconds] = useState("15");
  const [roi, setRoi] = useState<Roi | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  const overlayRef = useRef<HTMLDivElement>(null);
  const photoUrlRef = useRef<string | null>(null);

  const takePhoto = useTakePhoto(deviceId);
  const focusMutation = useSetFocus(deviceId);

  const formatValue = (value: number | null | undefined) =>
    value != null ? formatNumber(value) : "?";

  const capture = useCallback(async () => {
    if (takePhoto.isPending) return;
    try {
      const result = await takePhoto.mutateAsync(Number(quality));
      if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
      photoUrlRef.current = result.url;
      setPhoto(result);
    } catch (error) {
      toast.error(translate("cameras.photo.failed", { error: backendErrorMessage(error) }));
    }
  }, [takePhoto, quality]);

  // Auto refresh loop; skipped while a capture is already running
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      void capture();
    }, Number(refreshSeconds) * 1000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshSeconds, capture]);

  // Reset per-camera state and release the blob URL
  useEffect(() => {
    setPhoto(null);
    setRoi(null);
    setAutoRefresh(false);
    return () => {
      if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
      photoUrlRef.current = null;
    };
  }, [deviceId]);

  const pointerPosition = (event: React.PointerEvent): { x: number; y: number } => {
    const rect = overlayRef.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
  };

  const onPointerDown = (event: React.PointerEvent) => {
    if (!photo) return;
    event.preventDefault();
    overlayRef.current?.setPointerCapture(event.pointerId);
    const { x, y } = pointerPosition(event);
    setDrag({ startX: x, startY: y, currentX: x, currentY: y });
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (!drag) return;
    const { x, y } = pointerPosition(event);
    setDrag({ ...drag, currentX: x, currentY: y });
  };

  const onPointerUp = () => {
    if (!drag) return;
    const candidate = dragToRoi(drag);
    setDrag(null);
    // Ignore accidental clicks; require a real rectangle
    if (candidate.width < 0.02 || candidate.height < 0.02) return;
    setRoi(roundRoi(candidate));
  };

  const focusOnRoi = async () => {
    if (!roi) return;
    try {
      const result = await focusMutation.mutateAsync({ mode: "auto", roi });
      if (result.success && result.autofocus?.locked) {
        toast.success(
          t("cameras.photo.focusedOnRoi", {
            position: formatValue(result.autofocus.lens_position),
            duration: formatValue(result.autofocus.duration_ms),
          }),
        );
      } else {
        toast.warning(
          result.error ??
            t("cameras.focus.notConverged", {
              state: result.autofocus?.af_state_name ?? t("cameras.focus.unknownState"),
            }),
        );
      }
      await capture();
    } catch (error) {
      toast.error(t("cameras.focus.failed", { error: backendErrorMessage(error) }));
    }
  };

  const activeRect = drag ? dragToRoi(drag) : roi;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CameraIcon className="h-5 w-5" />
              {t("cameras.photo.title")}
            </CardTitle>
            <CardDescription>
              {t("cameras.photo.description")}
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label htmlFor="photo-quality" className="text-sm">
                {t("cameras.control.quality")}
              </Label>
              <Select value={quality} onValueChange={setQuality}>
                <SelectTrigger id="photo-quality" className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["50", "70", "85", "95"].map((q) => (
                    <SelectItem key={q} value={q}>
                      {formatNumber(Number(q) / 100, { style: "percent" })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="photo-auto-refresh"
                checked={autoRefresh}
                onCheckedChange={setAutoRefresh}
              />
              <Label htmlFor="photo-auto-refresh" className="text-sm">
                {t("cameras.photo.autoRefresh")}
              </Label>
              <Select value={refreshSeconds} onValueChange={setRefreshSeconds}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["10", "15", "30", "60"].map((s) => (
                    <SelectItem key={s} value={s}>
                      {t("cameras.photo.seconds", { seconds: s })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={capture} disabled={takePhoto.isPending}>
              {takePhoto.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CameraIcon className="mr-2 h-4 w-4" />
              )}
              {t("cameras.photo.take")}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          ref={overlayRef}
          className="relative w-full select-none overflow-hidden rounded-md border bg-muted"
          style={{ cursor: photo ? "crosshair" : "default" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          {photo ? (
            <img
              src={photo.url}
              alt={t("cameras.photo.alt")}
              className="block max-h-[70vh] w-full object-contain"
              draggable={false}
            />
          ) : (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
              {takePhoto.isPending ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {t("cameras.photo.capturing")}
                </span>
              ) : (
                t("cameras.photo.empty", { button: t("cameras.photo.take") })
              )}
            </div>
          )}
          {photo && activeRect && (
            <div
              className="pointer-events-none absolute border-2 border-yellow-400 bg-yellow-400/10"
              style={{
                left: `${activeRect.x * 100}%`,
                top: `${activeRect.y * 100}%`,
                width: `${activeRect.width * 100}%`,
                height: `${activeRect.height * 100}%`,
              }}
            />
          )}
          {photo && takePhoto.isPending && (
            <div className="absolute right-2 top-2 rounded bg-background/80 p-1">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {photo && (
            <span className="text-xs text-muted-foreground">
              {photo.takenAt.toLocaleTimeString(intlLocale)} ·{" "}
              {formatNumber(photo.size / 1024 / 1024, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              MB
              {photo.source
                ? ` · ${t("cameras.photo.source", { source: photo.source })}`
                : ""}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            {roi && (
              <>
                <Badge variant="outline" className="font-mono text-xs">
                  ROI x={roi.x} y={roi.y} w={roi.width} h={roi.height}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRoi(null)}
                  disabled={focusMutation.isPending}
                >
                  <X className="mr-1 h-3 w-3" />
                  {t("common.clear")}
                </Button>
                <Button
                  size="sm"
                  onClick={focusOnRoi}
                  disabled={focusMutation.isPending}
                >
                  {focusMutation.isPending ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <Crosshair className="mr-1 h-3 w-3" />
                  )}
                  {t("cameras.photo.focusOnRoi")}
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
